import { useState, useEffect, useCallback } from 'react';
import { useDashboardStore } from '@/stores/useDashboardStore';
import {
  getSecuritySummary,
  getFirewallGroups,
  getFirewallHitsByNode,
  getRulesPerNetwork,
  getDeniedTrafficTrend,
  getDeniedPacketsForPeriod,
  getDevHeaders,
} from '@/services/api';
import { API_BASE_URL } from '@/utils/constants';
import type {
  SecuritySummary,
  FirewallGroupItem,
  FirewallHitsByNode,
  RulesPerNetwork,
  DeniedTrafficPoint,
  DeniedPacketsPeriod,
} from '@/types/metrics';

export type { SecuritySummary };

export interface FirewallPolicyItem {
  id: string;
  name: string;
  nodeName: string | null;
  type: string | null;
  segmentCategory: string | null;
  firewallEnabled: boolean | null;
  firewallInbound: string | null;
  firewallOutbound: string | null;
  firewallForward: string | null;
  firewallMasquerade: boolean | null;
  cidr: string | null;
  vlanId: number | null;
}

interface UseFirewallParams {
  search?: string;
  segment?: string;
  firewallEnabled?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export function useSecuritySummary() {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [summary, setSummary] = useState<SecuritySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const data = await getSecuritySummary();
        if (!cancelled) {
          setSummary(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch security summary'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return { summary, loading, error };
}

export function useFirewallPolicies(params: UseFirewallParams = {}) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [policies, setPolicies] = useState<FirewallPolicyItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const depsKey = JSON.stringify(params);

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      if (params.segment) qs.set('segment', params.segment);
      if (params.firewallEnabled) qs.set('firewallEnabled', params.firewallEnabled);
      if (params.page) qs.set('page', String(params.page));
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.sortBy) qs.set('sortBy', params.sortBy);
      if (params.sortOrder) qs.set('sortOrder', params.sortOrder);

      const res = await window.fetch(`${API_BASE_URL}/api/security/policies?${qs}`, { credentials: 'include', headers: getDevHeaders() });
      if (!res.ok) throw new Error('Failed to fetch firewall policies');
      const data = await res.json();
      setPolicies(data.policies);
      setTotal(data.total);
      setTotalPages(data.totalPages);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch firewall policies'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, refreshKey]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  return { policies, total, totalPages, loading, error };
}

export function useFirewallGroups(params: {
  ids?: string[];
  search?: string;
  hasOverlyPermissive?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
} = {}) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [groups, setGroups] = useState<FirewallGroupItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const depsKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const data = await getFirewallGroups(params);
        if (!cancelled) {
          setGroups(data.groups);
          setTotal(data.total);
          setTotalPages(data.totalPages);
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, refreshKey]);

  return { groups, total, totalPages, loading };
}

export function useFirewallHitsByNode() {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [data, setData] = useState<FirewallHitsByNode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const result = await getFirewallHitsByNode();
        if (!cancelled) setData(result);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return { data, loading };
}

export function useRulesPerNetwork(segment?: string) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [data, setData] = useState<RulesPerNetwork[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const result = await getRulesPerNetwork(segment);
        if (!cancelled) setData(result);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [refreshKey, segment]);

  return { data, loading };
}

export function useDeniedTrafficTrend(period = '30d', networkName?: string, nodeName?: string) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [data, setData] = useState<DeniedTrafficPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const result = await getDeniedTrafficTrend(period, networkName, nodeName);
        if (!cancelled) setData(result);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [refreshKey, period, networkName, nodeName]);

  return { data, loading };
}

export function useDeniedPacketsForPeriod(period = '24h') {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [data, setData] = useState<DeniedPacketsPeriod | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const result = await getDeniedPacketsForPeriod(period);
        if (!cancelled) setData(result);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [refreshKey, period]);

  return { data, loading };
}
